import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import UIElement from '@ckeditor/ckeditor5-engine/src/view/uielement';
import { toWidget } from '@ckeditor/ckeditor5-widget/src/utils';
import React from 'react';
import ReactDOM from 'react-dom';
import { SimpleVariablesAsTags } from 'components/molecules/variables/simple-variables-as-tags';
import styles from './variable.module.css';
import { VariablesUI } from './variables-ui';

export class SimpleVariables extends Plugin {
  static get requires() {
    return [VariablesUI];
  }

  static get pluginName(): string {
    return 'SimpleVariables';
  }

  private simpleVariables(
    fieldName,
    fallback,
    container,
    modelItem,
    writer,
    fields,
    type,
    templateType,
    triggerIdentifier,
    triggerUuid,
  ) {
    ReactDOM.render(
      <SimpleVariablesAsTags
        fieldName={fieldName}
        fallback={fallback}
        editor={this.editor}
        modelItem={modelItem}
        writer={writer}
        templateType={templateType}
        meta={{ type, triggerIdentifier, fields, triggerUuid }}
      />,
      container,
    );
  }

  init() {
    this.defineSchema();
  }

  private defineSchema() {
    const { schema } = this.editor.model;

    const renderSimpleVariables = this.simpleVariables.bind(this);

    schema.register('simpleVariables', {
      inheritAllFrom: '$inlineObject',
      allowAttributes: [
        'fieldName',
        'fieldNameForBackend',
        'fallback',
        'fields',
        'templateType',
        'type',
        'triggerIdentifier',
        'triggerUuid',
      ],
    });

    this.editor.conversion.for('dataDowncast').elementToElement({
      model: 'simpleVariables',
      view: (modelItem, { writer }) => {
        const fieldName = modelItem.getAttribute('fieldName');
        const fieldNameForBackend =
          modelItem.getAttribute('fieldNameForBackend') ?? fieldName;
        const fallback = modelItem.getAttribute('fallback');
        const templateType = modelItem.getAttribute('templateType');
        const triggerIdentifier = modelItem.getAttribute('triggerIdentifier');
        const type = modelItem.getAttribute('type');
        const triggerUuid = modelItem.getAttribute('triggerUuid');

        const conditionalVariablesView = writer.createContainerElement('span', {
          class: 'simpleVariables',
          'data-fieldname': fieldName,
          'data-fieldnameforbackend': fieldNameForBackend,
          'data-fallback': fallback,
          'data-templateType': templateType,
          'data-type': type,
          'data-triggerIdentifier': triggerIdentifier,
          'data-triggerUuid': triggerUuid,
        });

        const ConditionalVariablesEl = writer.createRawElement(
          'span',
          {},
          (domElement) => {
            domElement.innerHTML = `{{{fallback ${fieldNameForBackend} "${fallback}"}}}`;

            return domElement;
          },
        );

        writer.insert(
          writer.createPositionAt(conditionalVariablesView, 0),
          ConditionalVariablesEl,
        );

        return conditionalVariablesView;
      },
    });

    this.editor.conversion.for('editingDowncast').elementToElement({
      model: 'simpleVariables',
      view: (modelItem, { writer }) => {
        const fieldName = modelItem.getAttribute('fieldName');
        const fallback = modelItem.getAttribute('fallback');
        const fields = modelItem.getAttribute('fields');
        const templateType = modelItem.getAttribute('templateType');
        const type = modelItem.getAttribute('type');
        const triggerIdentifier = modelItem.getAttribute('triggerIdentifier');
        const triggerUuid = modelItem.getAttribute('triggerUuid');

        // We must make sure that this extra properties dont get bloated.
        // Not everything must be solved through this. PLEASE add extra props only if it is absolutely necessary

        const conditionalVariablesView = writer.createContainerElement('span', {
          class: `simpleVariables ${styles['simple-variables']}`,
          'data-cke-ignore-events': true,
          'data-templateType': templateType,
          'data-type': type,
          'data-triggerIdentifier': triggerIdentifier,
          'data-triggerUuid': triggerUuid,
        });
        const reactWrapper = writer.createUIElement(
          'span',
          {
            'data-templateType': templateType,
            'data-triggerIdentifier': triggerIdentifier,
            'data-type': type,
            'data-triggeruuid': triggerUuid,
          },
          function (this: UIElement, domDocument: any) {
            const domElement = this.toDomElement(domDocument);

            renderSimpleVariables(
              fieldName,
              fallback,
              domElement,
              modelItem,
              writer,
              fields,
              type,
              templateType,
              triggerIdentifier,
              triggerUuid,
            );

            return domElement;
          },
        );

        writer.insert(
          writer.createPositionAt(conditionalVariablesView, 0),
          reactWrapper,
        );

        return toWidget(conditionalVariablesView, writer);
      },
    });

    this.editor.conversion.for('upcast').elementToElement({
      view: {
        name: 'span',
        classes: ['simpleVariables'],
      },
      model: (viewElement, { writer }) => {
        const fieldName = viewElement.getAttribute('data-fieldname');
        const fieldNameForBackend = viewElement.getAttribute(
          'data-fieldnameforbackend',
        );
        const fallback = viewElement.getAttribute('data-fallback');
        const fields = viewElement.getAttribute('fields');
        const templateType = viewElement.getAttribute('data-templatetype');
        const type = viewElement.getAttribute('data-type');
        const triggerIdentifier = viewElement.getAttribute(
          'data-triggeridentifier',
        );
        const triggerUuid = viewElement.getAttribute('data-triggeruuid');

        return writer.createElement('simpleVariables', {
          fieldName,
          fieldNameForBackend,
          fallback,
          fields,
          templateType,
          type,
          triggerIdentifier,
          triggerUuid,
        });
      },
    });
  }
}
